/**
 * r41 — Fixed-format Pivot Report
 *
 * Live pivot of applications:
 *   rows = Unit (BPO Team) → Country Living in → Session Month
 *   cols = Application Statuses (live from Core Settings)
 *
 * Filters: Team(s), University(ies), Date range. CSV export. The shape of the
 * underlying response is documented in `POST /api/reports/pivot/applications`.
 */
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useAuth } from '../../App';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Loader2, Filter, Download, RefreshCw, Columns3, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { MultiSearchableSelect } from '../ui/searchable-select';

const todayISO = () => new Date().toISOString().slice(0, 10);

const groupByUnit = (rows) => {
  const map = new Map();
  for (const r of rows) {
    if (!map.has(r.unit)) map.set(r.unit, new Map());
    const byCountry = map.get(r.unit);
    if (!byCountry.has(r.country)) byCountry.set(r.country, []);
    byCountry.get(r.country).push(r);
  }
  return map;
};

const PivotApplicationsReport = () => {
  const { api, user } = useAuth();
  const userId = user?.id || 'anon';
  const colPrefKey = `pivot.cols.v1.${userId}`;

  const [teams, setTeams] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [users, setUsers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [allStatuses, setAllStatuses] = useState([]);
  const [teamIds, setTeamIds] = useState([]);
  const [universityIds, setUniversityIds] = useState([]);
  const [agentIds, setAgentIds] = useState([]);
  const [countryIds, setCountryIds] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [autoLoaded, setAutoLoaded] = useState(false);
  // r51 — AI assist NL prompt
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  // r51 — auto-hide zero-count status columns by default; user can still
  // un-hide via the Columns popover.
  const [autoHideZero, setAutoHideZero] = useState(true);

  // r45 — column visibility persisted per-user in localStorage. Hidden status
  // columns are stored as a Set of column names; row-key columns + Grand Total
  // are toggled with a separate flag. Falsy/missing entries default to "show".
  const [hiddenCols, setHiddenCols] = useState(() => {
    try {
      const raw = localStorage.getItem(colPrefKey);
      if (!raw) return new Set();
      return new Set(JSON.parse(raw));
    } catch {
      return new Set();
    }
  });
  // re-load preferences if user changes
  useEffect(() => {
    try {
      const raw = localStorage.getItem(colPrefKey);
      setHiddenCols(raw ? new Set(JSON.parse(raw)) : new Set());
    } catch {
      setHiddenCols(new Set());
    }
  }, [colPrefKey]);
  const persistHidden = (next) => {
    setHiddenCols(next);
    try {
      localStorage.setItem(colPrefKey, JSON.stringify([...next]));
    } catch {
      // silently ignore (private mode etc.)
    }
  };
  const toggleHidden = (key) => {
    const n = new Set(hiddenCols);
    if (n.has(key)) n.delete(key);
    else n.add(key);
    persistHidden(n);
  };
  const showAll = () => persistHidden(new Set());

  // Load filter options + initial run on mount
  useEffect(() => {
    (async () => {
      try {
        const [tRes, uRes, usersRes, cRes, sRes] = await Promise.all([
          api().get('/teams'),
          api().get('/master-data?type=university'),
          api().get('/users'),
          api().get('/master-data?type=country'),
          api().get('/master-data?type=application_status'),
        ]);
        setTeams(Array.isArray(tRes.data) ? tRes.data : tRes.data?.items || []);
        setUniversities(uRes.data || []);
        setUsers(Array.isArray(usersRes.data) ? usersRes.data : usersRes.data?.items || []);
        setCountries(cRes.data || []);
        setAllStatuses(sRes.data || []);
      } catch (e) {
        console.error('failed to load filters', e);
      }
    })();
  }, []); // eslint-disable-line

  // r44 — Auto-refire on any filter change so the user never has to remember
  // to click "Run". Debounced 350ms so multi-toggle interactions don't thrash.
  const debounceRef = useRef(null);
  useEffect(() => {
    if (!api) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      run();
      setAutoLoaded(true);
    }, autoLoaded ? 350 : 0);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(teamIds), JSON.stringify(universityIds), JSON.stringify(agentIds),
      JSON.stringify(countryIds), JSON.stringify(statusFilter), fromDate, toDate]);

  // When the user narrows by Team(s), automatically keep only team members
  // that belong to those teams. If no team filter is set, show everyone.
  const availableUsers = useMemo(() => {
    if (!teamIds.length) return users;
    const keep = new Set(teamIds);
    return users.filter((u) => u.team_id && keep.has(u.team_id));
  }, [users, teamIds]);

  // If the team filter changes and some previously-selected agents are no
  // longer in the allowed set, drop them so the UI stays consistent.
  useEffect(() => {
    if (!teamIds.length) return;
    const allowed = new Set(availableUsers.map((u) => u.id));
    setAgentIds((prev) => prev.filter((id) => allowed.has(id)));
  }, [availableUsers, teamIds]);

  const run = async () => {
    setLoading(true);
    try {
      const res = await api().post('/reports/pivot/applications', {
        team_ids: teamIds.length ? teamIds : null,
        university_ids: universityIds.length ? universityIds : null,
        agent_ids: agentIds.length ? agentIds : null,
        country_ids: countryIds.length ? countryIds : null,
        statuses: statusFilter.length ? statusFilter : null,
        from_date: fromDate || null,
        to_date: toDate || null,
      });
      setReport(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate pivot report');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setTeamIds([]);
    setUniversityIds([]);
    setAgentIds([]);
    setCountryIds([]);
    setStatusFilter([]);
    setFromDate('');
    setToDate('');
    setTimeout(run, 50);
  };

  // r51 — AI assist: send the natural-language prompt, then apply the parsed
  // filters into the same state the rule-builder uses so the user can tweak.
  const submitAi = async () => {
    const p = (aiPrompt || '').trim();
    if (p.length < 3) return;
    setAiBusy(true);
    try {
      const res = await api().post('/reports/pivot/applications/nl-to-filters', { prompt: p });
      const f = res.data?.filters || {};
      setTeamIds(f.team_ids || []);
      setAgentIds(f.agent_ids || []);
      setUniversityIds(f.university_ids || []);
      setCountryIds(f.country_ids || []);
      setStatusFilter(f.statuses || []);
      setFromDate(f.from_date || '');
      setToDate(f.to_date || '');
      // If the AI suggested specific status columns to show, un-hide those
      // and hide the rest (one-shot — user can still toggle the popover).
      if (Array.isArray(f.show_columns) && f.show_columns.length) {
        const wanted = new Set(f.show_columns.map((s) => s.toLowerCase()));
        setAutoHideZero(false); // explicit pick wins
        const next = new Set();
        for (const c of (allStatuses || [])) {
          if (!wanted.has((c.name || '').toLowerCase())) next.add(`status:${c.name}`);
        }
        persistHidden(next);
      }
      setAiOpen(false);
      setAiPrompt('');
      toast.success('Filters applied');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI parser failed');
    } finally {
      setAiBusy(false);
    }
  };

  const exportCsv = () => {
    if (!report) return;
    const cols = report.status_order || [];
    const lines = [];
    lines.push(['Team', 'Country Living in', 'Session Month', ...cols, 'Grand Total'].join(','));
    const grouped = groupByUnit(report.rows || []);
    const countryTotals = new Map(
      (report.country_totals || []).map((c) => [`${c.unit}|${c.country}`, c]),
    );
    const unitTotals = new Map((report.unit_totals || []).map((u) => [u.unit, u]));
    for (const [unit, byCountry] of grouped.entries()) {
      for (const [country, rows] of byCountry.entries()) {
        for (const r of rows) {
          lines.push(
            [unit, country, r.session, ...cols.map((c) => r.counts[c] || 0), r.total]
              .map((v) => `"${String(v).replace(/"/g, '""')}"`)
              .join(','),
          );
        }
        const ct = countryTotals.get(`${unit}|${country}`);
        if (ct) {
          lines.push(
            ['', `${country} Total`, '', ...cols.map((c) => ct.counts[c] || 0), ct.total]
              .map((v) => `"${v}"`)
              .join(','),
          );
        }
      }
      const ut = unitTotals.get(unit);
      if (ut) {
        lines.push(
          [`${unit} Total`, '', '', ...cols.map((c) => ut.counts[c] || 0), ut.total]
            .map((v) => `"${v}"`)
            .join(','),
        );
      }
    }
    const grand = report.grand_total || { counts: {}, total: 0 };
    lines.push(
      ['Grand Total', '', '', ...cols.map((c) => grand.counts[c] || 0), grand.total]
        .map((v) => `"${v}"`)
        .join(','),
    );
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pivot-applications-${todayISO()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // r45 — visible-column lists. Status columns can be hidden individually; the
  // 4 row-key columns (Unit, Country, Session, Grand Total) can be hidden too.
  // r51 — also auto-hide zero-count status columns by default so the table
  // shows only what's relevant for the current filter scope.
  const allCols = report?.status_order || [];
  const nonZero = new Set(report?.nonzero_statuses || []);
  const visibleCols = allCols.filter((c) => {
    if (hiddenCols.has(`status:${c}`)) return false;
    if (autoHideZero && !nonZero.has(c)) return false;
    return true;
  });
  const showUnit = !hiddenCols.has('row:unit');
  const showCountry = !hiddenCols.has('row:country');
  const showSession = !hiddenCols.has('row:session');
  const showGrand = !hiddenCols.has('row:grand');
  const totalCustomizable = allCols.length + 4;
  const hiddenCount = hiddenCols.size;

  const cols = visibleCols;

  // Pre-compute lookup tables for sub-totals
  const countryTotals = useMemo(() => {
    const m = new Map();
    for (const c of report?.country_totals || []) m.set(`${c.unit}|${c.country}`, c);
    return m;
  }, [report]);
  const unitTotals = useMemo(() => {
    const m = new Map();
    for (const u of report?.unit_totals || []) m.set(u.unit, u);
    return m;
  }, [report]);

  const grouped = useMemo(() => groupByUnit(report?.rows || []), [report]);

  return (
    <div className="space-y-6" data-testid="pivot-report-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Applications Pivot Report</h2>
          <p className="text-sm text-muted-foreground">
            Live count of applications grouped by Team → Country → Session, sliced by status.
          </p>
        </div>
        <div className="flex gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" data-testid="pivot-columns-btn">
                <Columns3 className="w-4 h-4 mr-2" />
                Columns
                {hiddenCount > 0 && (
                  <Badge variant="secondary" className="ml-2 h-5 px-1.5 text-[10px]">
                    {totalCustomizable - hiddenCount}/{totalCustomizable}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 p-0">
              <div className="flex items-center justify-between border-b px-3 py-2">
                <span className="text-sm font-semibold">Columns</span>
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={showAll}>
                  Show all
                </Button>
              </div>
              <div className="max-h-80 overflow-y-auto p-2 space-y-1">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase px-2 pt-1">Row keys</p>
                {[
                  { key: 'row:unit', label: 'Team' },
                  { key: 'row:country', label: 'Country Living in' },
                  { key: 'row:session', label: 'Session Month' },
                  { key: 'row:grand', label: 'Grand Total' },
                ].map((it) => (
                  <label key={it.key} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/60 cursor-pointer">
                    <Checkbox
                      checked={!hiddenCols.has(it.key)}
                      onCheckedChange={() => toggleHidden(it.key)}
                    />
                    <span className="text-sm">{it.label}</span>
                  </label>
                ))}
                {allCols.length > 0 && (
                  <>
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase px-2 pt-2">Status columns</p>
                    {allCols.map((c) => {
                      const k = `status:${c}`;
                      return (
                        <label key={k} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/60 cursor-pointer">
                          <Checkbox
                            checked={!hiddenCols.has(k)}
                            onCheckedChange={() => toggleHidden(k)}
                          />
                          <span className="text-sm truncate">{c}</span>
                        </label>
                      );
                    })}
                  </>
                )}
              </div>
              <p className="px-3 py-2 text-[10px] text-muted-foreground border-t">
                Saved automatically per user.
              </p>
            </PopoverContent>
          </Popover>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={!report}>
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button size="sm" onClick={run} disabled={loading} data-testid="pivot-run-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Run
          </Button>
        </div>
      </div>

      {/* r51 — AI assist row */}
      <Card className="border-dashed">
        <CardContent className="p-3 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-500 shrink-0" />
          <Input
            placeholder='Ask in plain English — e.g. "applications from IEO team for Sunderland Q3 — show fee paid and offer received"'
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !aiBusy) submitAi(); }}
            className="flex-1 border-0 focus-visible:ring-1 focus-visible:ring-amber-300"
            data-testid="pivot-ai-prompt"
          />
          <Button
            size="sm"
            onClick={submitAi}
            disabled={aiBusy || (aiPrompt || '').trim().length < 3}
            className="bg-amber-500 hover:bg-amber-500"
            data-testid="pivot-ai-submit"
          >
            {aiBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
            Apply
          </Button>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs flex items-center gap-1.5">
              <Filter className="w-3 h-3" />
              Teams
            </Label>
            <MultiSearchableSelect
              value={teamIds}
              onValueChange={setTeamIds}
              options={teams.map((t) => ({ value: t.id, label: t.name }))}
              placeholder="All teams"
              searchPlaceholder="Search teams…"
              emptyText="No teams configured."
              testid="pivot-team-filter"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs flex items-center gap-1.5">
              <Filter className="w-3 h-3" />
              Team Members
              {teamIds.length > 0 && (
                <span className="text-[10px] text-muted-foreground">(in selected teams)</span>
              )}
            </Label>
            <MultiSearchableSelect
              value={agentIds}
              onValueChange={setAgentIds}
              options={availableUsers.map((u) => ({
                value: u.id,
                label: u.team_name ? `${u.name} · ${u.team_name}` : u.name,
              }))}
              placeholder="All members"
              searchPlaceholder="Search members…"
              emptyText={teamIds.length ? 'No members in selected teams.' : 'No users.'}
              testid="pivot-agent-filter"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs flex items-center gap-1.5">
              <Filter className="w-3 h-3" />
              Universities
            </Label>
            <MultiSearchableSelect
              value={universityIds}
              onValueChange={setUniversityIds}
              options={universities.map((u) => ({ value: u.id, label: u.name }))}
              placeholder="All universities"
              searchPlaceholder="Search universities…"
              emptyText="No universities configured."
              testid="pivot-uni-filter"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs flex items-center gap-1.5">
              <Filter className="w-3 h-3" />
              Country (Nationality)
            </Label>
            <MultiSearchableSelect
              value={countryIds}
              onValueChange={setCountryIds}
              options={countries.map((c) => ({ value: c.id, label: c.name }))}
              placeholder="All countries"
              searchPlaceholder="Search countries…"
              emptyText="No countries configured."
              testid="pivot-country-filter"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs flex items-center gap-1.5">
              <Filter className="w-3 h-3" />
              Status
            </Label>
            <MultiSearchableSelect
              value={statusFilter}
              onValueChange={setStatusFilter}
              options={(allStatuses || []).map((s) => ({ value: s.name, label: s.name }))}
              placeholder="All statuses"
              searchPlaceholder="Search statuses…"
              emptyText="No statuses configured."
              testid="pivot-status-filter"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">From date</Label>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              data-testid="pivot-from-date"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">To date</Label>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              data-testid="pivot-to-date"
            />
          </div>
          <div className="lg:col-span-7 md:col-span-3 flex items-center gap-2 pt-1">
            <Button size="sm" variant="ghost" onClick={reset}>Clear filters</Button>
            <label className="text-xs flex items-center gap-1.5 ml-2 cursor-pointer">
              <Checkbox
                checked={autoHideZero}
                onCheckedChange={(v) => setAutoHideZero(!!v)}
                data-testid="pivot-autohide-zero"
              />
              Auto-hide empty status columns
            </label>
            {report && (
              <Badge variant="outline" className="ml-auto">
                {report.applications_in_scope} applications · {visibleCols.length}/{allCols.length} statuses shown
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Pivot Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {loading && (
            <div className="p-12 flex items-center justify-center text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              Crunching numbers…
            </div>
          )}
          {!loading && report && (
            <table className="w-full text-sm" data-testid="pivot-table">
              <thead>
                <tr className="bg-slate-100 dark:bg-slate-800">
                  {(showUnit || showCountry || showSession) && (
                    <th
                      colSpan={[showUnit, showCountry, showSession].filter(Boolean).length}
                      className="px-3 py-2 text-left font-semibold border-b border-r border-slate-200 dark:border-slate-700"
                    >
                      COUNTA of Applied University
                    </th>
                  )}
                  <th
                    colSpan={cols.length + (showGrand ? 1 : 0)}
                    className="px-3 py-2 text-left font-semibold border-b border-slate-200 dark:border-slate-700"
                  >
                    Status of Offer
                  </th>
                </tr>
                <tr className="bg-slate-200/70 dark:bg-slate-800">
                  {showUnit && (
                    <th className="px-3 py-2 text-left font-semibold border-b border-slate-300 dark:border-slate-700">Team</th>
                  )}
                  {showCountry && (
                    <th className="px-3 py-2 text-left font-semibold border-b border-slate-300 dark:border-slate-700">Country Living in</th>
                  )}
                  {showSession && (
                    <th className="px-3 py-2 text-left font-semibold border-b border-r border-slate-300 dark:border-slate-700">Session Month</th>
                  )}
                  {cols.map((c) => (
                    <th key={c} className="px-3 py-2 text-center font-semibold border-b border-slate-300 dark:border-slate-700 whitespace-nowrap">
                      {c}
                    </th>
                  ))}
                  {showGrand && (
                    <th className="px-3 py-2 text-center font-semibold border-b border-l border-slate-300 dark:border-slate-700 whitespace-nowrap">
                      Grand Total
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {Array.from(grouped.entries()).map(([unit, byCountry]) => {
                  const ut = unitTotals.get(unit);
                  return (
                    <React.Fragment key={unit}>
                      {Array.from(byCountry.entries()).map(([country, rows]) => {
                        const ct = countryTotals.get(`${unit}|${country}`);
                        return (
                          <React.Fragment key={`${unit}-${country}`}>
                            {rows.map((r, idx) => (
                              <tr key={`${unit}-${country}-${r.session}-${idx}`} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900/40">
                                {showUnit && (
                                  <td className="px-3 py-1.5 text-slate-700 dark:text-slate-300">
                                    {idx === 0 && rows[0] === r ? unit : ''}
                                  </td>
                                )}
                                {showCountry && (
                                  <td className="px-3 py-1.5 text-slate-700 dark:text-slate-300">
                                    {idx === 0 ? country : ''}
                                  </td>
                                )}
                                {showSession && (
                                  <td className="px-3 py-1.5 border-r border-slate-200 dark:border-slate-700">{r.session}</td>
                                )}
                                {cols.map((c) => (
                                  <td key={c} className="px-3 py-1.5 text-center tabular-nums">
                                    {r.counts[c] ? r.counts[c] : <span className="text-slate-300 dark:text-slate-600">·</span>}
                                  </td>
                                ))}
                                {showGrand && (
                                  <td className="px-3 py-1.5 text-center font-semibold border-l border-slate-200 dark:border-slate-700 tabular-nums">
                                    {r.total}
                                  </td>
                                )}
                              </tr>
                            ))}
                            {ct && (
                              <tr className="bg-slate-50 dark:bg-slate-900/60 font-semibold border-b border-slate-200 dark:border-slate-700">
                                {showUnit && <td></td>}
                                {showCountry && <td className="px-3 py-1.5 text-slate-700 dark:text-slate-200">{country} Total</td>}
                                {showSession && <td className="border-r border-slate-200 dark:border-slate-700"></td>}
                                {cols.map((c) => (
                                  <td key={c} className="px-3 py-1.5 text-center tabular-nums">
                                    {ct.counts[c] || 0}
                                  </td>
                                ))}
                                {showGrand && (
                                  <td className="px-3 py-1.5 text-center border-l border-slate-200 dark:border-slate-700 tabular-nums">
                                    {ct.total}
                                  </td>
                                )}
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                      {ut && (
                        <tr className="bg-slate-100 dark:bg-slate-900 font-semibold border-b-2 border-slate-300 dark:border-slate-700">
                          {showUnit && <td className="px-3 py-1.5">{unit} Total</td>}
                          {showCountry && <td></td>}
                          {showSession && <td className="border-r border-slate-200 dark:border-slate-700"></td>}
                          {cols.map((c) => (
                            <td key={c} className="px-3 py-1.5 text-center tabular-nums">{ut.counts[c] || 0}</td>
                          ))}
                          {showGrand && (
                            <td className="px-3 py-1.5 text-center border-l border-slate-200 dark:border-slate-700 tabular-nums">{ut.total}</td>
                          )}
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {report.grand_total && (
                  <tr className="bg-slate-900 text-white font-bold">
                    {showUnit && <td className="px-3 py-2">Grand Total</td>}
                    {showCountry && <td></td>}
                    {showSession && <td className="border-r border-slate-700"></td>}
                    {cols.map((c) => (
                      <td key={c} className="px-3 py-2 text-center tabular-nums">{report.grand_total.counts[c] || 0}</td>
                    ))}
                    {showGrand && (
                      <td className="px-3 py-2 text-center border-l border-slate-700 tabular-nums">{report.grand_total.total}</td>
                    )}
                  </tr>
                )}
                {report.rows.length === 0 && (
                  <tr>
                    <td colSpan={cols.length + 4} className="px-3 py-12 text-center text-muted-foreground">
                      No applications match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PivotApplicationsReport;
